import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock, Mail, Loader2, LogIn } from "lucide-react";
import { setSession, formatApiError } from "../lib/admin";
import { usePageTitle, FlagDot } from "../components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  usePageTitle("Espace équipe — Connexion");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setSession(data.token, data.user);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-brand-deep px-6 py-24">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mb-4 flex items-center justify-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-gold">
            <FlagDot /> DOC TOGO — Back office
          </div>
          <h1 className="font-display text-3xl font-extrabold text-white">Connexion équipe</h1>
          <p className="mt-2 text-sm text-white/50">Réservé à l'équipe DOC TOGO.</p>
        </div>
        <form onSubmit={submit} data-testid="admin-login-form" className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-md">
          {error && (
            <div data-testid="admin-login-error" className="mb-5 rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50" htmlFor="login-email">Email</label>
          <div className="relative mb-5">
            <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
            <input
              id="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              placeholder="vous@doc-togo.com"
              className="w-full rounded-xl border border-white/15 bg-white/5 py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/25 outline-none transition-[border-color] duration-300 focus:border-gold"
            />
          </div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50" htmlFor="login-password">Mot de passe</label>
          <div className="relative mb-7">
            <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
            <input
              id="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              placeholder="••••••••"
              className="w-full rounded-xl border border-white/15 bg-white/5 py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/25 outline-none transition-[border-color] duration-300 focus:border-gold"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
            Se connecter
          </button>
        </form>
        <p className="mt-6 text-center text-xs text-white/35">
          <Link to="/" data-testid="admin-login-back" className="transition-colors hover:text-white">← Retour au site</Link>
        </p>
      </div>
    </div>
  );
}
